import React, { createContext, useCallback, useContext, useMemo, useState, ReactNode } from 'react';
import { PROXY_INBOX_EMAILS, type ProxyInboxEmail } from '../data/mockProxyEmails';
import { WAYNE_INBOX_EMAILS } from '../data/wayneInboxEmails';
import { type ProxyJob, PROXY_JOBS, PROXY_SEED_TODAY } from '../data/mockProxyJobs';
import { pullFollowUpReport } from '../utils/followUpReportPull';
import { OUTREACH_KIND } from '../utils/proxyFollowUpTracker';
import { classifyEmail, type ProxyEmailCategory } from '../utils/proxyEmailClassifier';

// Single shared source of truth for the Proxy + Fulfillment Email Inbox — the
// "shared mailbox Ace reads through" the Proxy/Fulfillment side nav both point
// at. Mirrors ProxyDataContext.tsx's idiom (immutable seed + localStorage diff
// overlay) rather than inventing a new persistence pattern.
//
// A thread groups every message that shares a job number within one origin —
// an original inbound email, any Ace-sent outbound (from approving a Proxy
// task), and any reply, all live together so "Wayne sent this / got this back"
// reads as one conversation instead of two disconnected emails.

export interface EmailMessage {
  id: string;
  direction: 'inbound' | 'outbound';
  from: string;
  fromEmail: string;
  to: string;
  cc: string[];
  date: string;
  body: string;
  sentByAce?: boolean;
  // Only set when a quick reply's subject was edited away from the thread's
  // default subject — shown as its own row in the message card so an edited
  // subject is visible, without touching the thread-level `subject` used for
  // the drawer header / list.
  subject?: string;
}

export interface EmailThread {
  id: string;
  origin: 'proxy' | 'fulfillment';
  subject: string;
  jobNumbers: string[];
  messages: EmailMessage[];
  // Proxy-only — computed off the thread's original message, undefined for
  // 'fulfillment' threads (Wayne's inbox was never routed through this).
  // The category in effect: a person's correction if there is one, else Ace's.
  category?: ProxyEmailCategory | null;
  aceCategory?: ProxyEmailCategory | null;
  correction?: CategoryCorrection;
  confidence?: number;
  routeTo?: string;
}

// The email a thread is about: its first incoming message. A reply thread
// opens with our own chase, so message one isn't always it.
export function leadMessage(thread: EmailThread): EmailMessage | undefined {
  return thread.messages.find(m => m.direction === 'inbound') ?? thread.messages[0];
}

// One conversation: the same subject, ignoring "Re:" / "Fwd:" and dash style.
function conversationKey(subject: string): string {
  return subject.toLowerCase().replace(/^((re|fwd?):\s*)+/, '').replace(/\s+[-–—]\s+/g, ' - ').trim();
}

// A person changing the category Ace gave an email, which is the input to Proxy
// Governance's classification accuracy. It's recorded only: Ace's routing
// and the tasks it already created stay as they were.
export interface CategoryCorrection {
  from: ProxyEmailCategory | null; // null: Ace couldn't classify it
  to: ProxyEmailCategory;
  by: string;
  at: string;
}

type CorrectionEntry = Omit<CategoryCorrection, 'from'>;

// Corrections the ICT team already made this morning, so the dashboard reads
// like a working day. Both are solicitor replies that really carry a job change.
const SEED_CORRECTIONS: Record<string, CorrectionEntry> = {
  'pinbox-048': { to: 'JOB_CHANGE', by: 'ICT Associate', at: '2026-06-01 09:12' },
  'pinbox-049': { to: 'JOB_CHANGE', by: 'ICT Associate', at: '2026-06-01 09:47' },
  'pinbox-013': { to: 'FOLLOW_UP_REPLY', by: 'ICT Associate', at: '2026-06-01 10:05' },
};

interface EmailInboxContextValue {
  threads: EmailThread[];
  // Origin: 'proxy' threads flattened back to ProxyInboxEmail shape — the
  // drop-in replacement for the static PROXY_INBOX_EMAILS import wherever
  // ProxyWorkflowContext.emails is built, so a typed-in reply is eligible to
  // classify into a new Intake Queue task exactly like a seeded email.
  proxyEmails: ProxyInboxEmail[];
  appendReply: (threadId: string, entry: { from: string; fromEmail: string; to: string; subject?: string; body: string }) => void;
  appendOutbound: (entry: {
    // A stable identity for a brand-new thread when no job number is known
    // yet (e.g. a "No Job Found" exception) — without this, every jobless
    // escalation would collapse into the same thread. Scan # or job # both
    // work; anything unique to the triggering record does.
    refKey: string;
    jobIds: string[];
    origin: 'proxy' | 'fulfillment';
    to: string;
    cc?: string[];
    subject: string;
    body: string;
    // Override the default "sent by Ace" sender — e.g. Fulfillment handing an
    // event to Proxy's inbox as its own message rather than something Ace sent.
    from?: string;
    fromEmail?: string;
    // Append to this exact thread (e.g. forwarding one audited email).
    threadId?: string;
  }) => void;
  // ICT-4 Reports: deleting a report email once it's been dealt with.
  deleteThread: (threadId: string) => void;
  restoreThreads: (threadIds: string[]) => void;
  // Choosing Ace's own category again clears the correction.
  setThreadCategory: (threadId: string, category: ProxyEmailCategory) => void;
}

// Shared-mailbox convention for Ace-sent mail: which team's address a
// message is sent "from" (Fulfillment's) or handed off "to" (an internal
// escalation's target team) — the same two addresses the Fulfillment/Proxy
// tabs are keyed on, per the Fulfillment tab containing fulfillment@betanxt.com.
export const TEAM_MAILBOX: Record<'wayne' | 'cary', string> = {
  wayne: 'fulfillment@betanxt.com',
  cary: 'proxymaterial@betanxt.com',
};

const EmailInboxContext = createContext<EmailInboxContextValue | null>(null);

const LS_KEY = 'opsai.emailinbox.v1';
const DELETED_KEY = 'opsai.emailinbox.deleted.v1';
const CATEGORY_KEY = 'opsai.emailinbox.categories.v1';

function safeParse(json: string | null): EmailThread[] {
  try {
    const v = JSON.parse(json || '');
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
}

function writeThrough(next: EmailThread[]) {
  if (typeof window !== 'undefined') window.localStorage.setItem(LS_KEY, JSON.stringify(next));
}

function uid() {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

// Mirrors mockProxyJobs.ts's proxyNowStamp() convention: a live action's date
// pins to PROXY_SEED_TODAY (the demo's fixed narrative "today"), not the real
// wall-clock date, so it stays comparable to every other seeded date and
// doesn't read as "in the future" — only the time-of-day is real.
function nowStamp() {
  const now = new Date();
  const hh = String(now.getHours()).padStart(2, '0');
  const mm = String(now.getMinutes()).padStart(2, '0');
  return `${PROXY_SEED_TODAY} ${hh}:${mm}`;
}

// A reply opens with the chase it answers (the job's last outreach before
// it), so the back and forth reads as one conversation.
function chaseAnswered(email: ProxyInboxEmail): EmailMessage | null {
  if (!/^re:/i.test(email.subject) || !email.relatedJobId) return null;
  const job = PROXY_JOBS.find(j => j.jobId === email.relatedJobId);
  const chase = job?.followUpHistory
    .filter(r => OUTREACH_KIND[r.type] && r.timestamp.slice(0, 10) <= email.date)
    .sort((a, b) => a.timestamp.localeCompare(b.timestamp))
    .pop();
  if (!chase) return null;
  return {
    id: `${email.id}-chase`, direction: 'outbound', from: chase.author, fromEmail: TEAM_MAILBOX.cary,
    to: chase.recipientEmail, cc: chase.ccEmails ?? [], date: chase.timestamp.slice(0, 10),
    subject: chase.subject, body: chase.bodyPreview,
  };
}

const seedEmailById = new Map(PROXY_INBOX_EMAILS.map(e => [e.id, e]));

function seedProxyThreads(): EmailThread[] {
  return PROXY_INBOX_EMAILS.map(email => {
    const cls = classifyEmail(email);
    const chase = chaseAnswered(email);
    return {
      id: email.id,
      origin: 'proxy',
      subject: email.subject,
      jobNumbers: email.relatedJobId ? [email.relatedJobId] : [],
      messages: [...(chase ? [chase] : []), {
        id: email.id,
        direction: 'inbound',
        from: email.from,
        fromEmail: email.fromEmail,
        to: email.to,
        cc: email.cc,
        date: email.date,
        body: email.body,
      }],
      category: cls.category,
      confidence: email.aceConfidence ?? Math.round(cls.confidence * 100),
      routeTo: cls.routeTo,
    };
  });
}


const FOLLOW_UP_REPORT_ID = 'proxy-report-followups';

// ICT-1/ICT-8: the Follow Ups job in Alteryx runs each morning and "automatically
// emails it to the ICT@betanxt.com email address for team dissemination":
// the Red and Yellow follow-ups, each grouped by solicitor, with ICT-7's
// exclusions applied (utils/followUpReportPull.ts). MATERIAL_FOLLOWUP reads
// this email and prepares a task for every row on it. Built once from the
// day's starting jobs, since it's the morning's report; jobs worked later in
// the day drop out of the tasks, not out of the report.
function buildFollowUpReportEmails(jobs: ProxyJob[]): { thread: EmailThread; emails: ProxyInboxEmail[] } {
  const rows = pullFollowUpReport(jobs, null, PROXY_SEED_TODAY).rows;
  const [y, m, d] = PROXY_SEED_TODAY.split('-');
  const subject = `ICT Follow Ups report ${m}.${d}.${y}`;
  const section = (band: 'Red' | 'Yellow') => {
    const inBand = rows.filter(r => r.band === band);
    const bySolicitor = new Map<string, string[]>();
    inBand.forEach(r => {
      const key = r.job.solicitor || 'Issuer';
      bySolicitor.set(key, [...(bySolicitor.get(key) || []), `  Job ${r.job.jobId}  ${r.job.issuer}  (${r.daysToMeeting}d to meeting)`]);
    });
    return `${band} Follow Ups (${inBand.length})\n${[...bySolicitor].map(([sol, lines]) => `${sol}\n${lines.join('\n')}`).join('\n')}`;
  };
  const body = rows.length ? `${section('Red')}\n\n${section('Yellow')}` : 'No jobs currently outstanding.';
  const baseMessage = {
    id: FOLLOW_UP_REPORT_ID,
    from: 'Alteryx', fromEmail: 'alteryx@betanxt.com',
    to: 'ict@betanxt.com', cc: [] as string[],
    date: PROXY_SEED_TODAY, body,
  };
  const cls = classifyEmail({ ...baseMessage, subject, relatedJobId: undefined });
  const thread: EmailThread = {
    id: FOLLOW_UP_REPORT_ID,
    origin: 'proxy',
    subject,
    jobNumbers: rows.map(r => r.job.jobId),
    messages: [{ ...baseMessage, direction: 'inbound' }],
    category: cls.category,
    confidence: Math.round(cls.confidence * 100),
    routeTo: cls.routeTo,
  };
  return { thread, emails: [{ ...baseMessage, subject }] };
}

// Fulfillment's own outside mail. Hand-offs between Fulfillment and Proxy
// aren't email any more; they're in-app messages (TeamMessagesContext).
function seedFulfillmentThreads(): EmailThread[] {
  return WAYNE_INBOX_EMAILS.map(email => ({
    id: email.id,
    origin: 'fulfillment',
    subject: email.subject,
    jobNumbers: [email.jobNumber],
    messages: [{
      id: email.id,
      direction: 'inbound',
      from: email.from.split('@')[0],
      fromEmail: email.from,
      to: email.to,
      cc: [],
      date: email.date,
      body: email.body,
    }],
  }));
}

export function EmailInboxProvider({ children }: { children: ReactNode }) {
  const seed = useMemo<EmailThread[]>(() => [...seedProxyThreads(), ...seedFulfillmentThreads()], []);
  const [overlay, setOverlay] = useState<EmailThread[]>(() => {
    if (typeof window === 'undefined') return [];
    return safeParse(window.localStorage.getItem(LS_KEY));
  });

  const [deleted, setDeleted] = useState<string[]>(() => {
    try { const v = JSON.parse(window.localStorage.getItem(DELETED_KEY) || '[]'); return Array.isArray(v) ? v : []; } catch { return []; }
  });
  const deleteThread = useCallback((threadId: string) => {
    setDeleted(prev => {
      const next = [...prev, threadId];
      try { window.localStorage.setItem(DELETED_KEY, JSON.stringify(next)); } catch { /* still works for this session */ }
      return next;
    });
  }, []);

  const restoreThreads = useCallback((threadIds: string[]) => {
    setDeleted(prev => {
      const next = prev.filter(id => !threadIds.includes(id));
      try { window.localStorage.setItem(DELETED_KEY, JSON.stringify(next)); } catch { /* still works for this session */ }
      return next;
    });
  }, []);

  // A null entry undoes a seeded correction.
  const [categoryOverrides, setCategoryOverrides] = useState<Record<string, CorrectionEntry | null>>(() => {
    try { const v = JSON.parse(window.localStorage.getItem(CATEGORY_KEY) || '{}'); return v && typeof v === 'object' ? v : {}; } catch { return {}; }
  });

  // The morning's report, built from the day's starting jobs.
  const followUpReport = useMemo(() => buildFollowUpReportEmails(PROXY_JOBS), []);

  // Overlay entries are additive-only diffs keyed by thread id: either a brand
  // new thread (created by appendOutbound when no existing thread matches the
  // job) or extra messages appended to a seeded thread's message list.
  const threads = useMemo<EmailThread[]>(() => {
    const overlayById = new Map(overlay.map(t => [t.id, t]));
    const merged = seed.map(t => {
      const patch = overlayById.get(t.id);
      if (!patch) return t;
      overlayById.delete(t.id);
      return { ...t, messages: [...t.messages, ...patch.messages] };
    });
    const withCategory = (t: EmailThread): EmailThread => {
      if (t.origin !== 'proxy') return t;
      const entry = t.id in categoryOverrides ? categoryOverrides[t.id] : SEED_CORRECTIONS[t.id];
      const ace = t.category ?? null;
      return entry
        ? { ...t, aceCategory: ace, category: entry.to, correction: { from: ace, ...entry } }
        : { ...t, aceCategory: ace };
    };
    return [...merged, ...overlayById.values(), followUpReport.thread].filter(t => !deleted.includes(t.id)).map(withCategory);
  }, [seed, overlay, followUpReport, deleted, categoryOverrides]);

  const setThreadCategory = useCallback((threadId: string, category: ProxyEmailCategory) => {
    const ace = threads.find(t => t.id === threadId)?.aceCategory ?? null;
    setCategoryOverrides(prev => {
      const next = { ...prev, [threadId]: category === ace ? null : { to: category, by: 'You', at: nowStamp() } };
      try { window.localStorage.setItem(CATEGORY_KEY, JSON.stringify(next)); } catch { /* still works for this session */ }
      return next;
    });
  }, [threads]);

  const proxyEmails = useMemo<ProxyInboxEmail[]>(() => {
    const out: ProxyInboxEmail[] = [];
    for (const t of threads) {
      if (t.origin !== 'proxy' || t.id === FOLLOW_UP_REPORT_ID) continue;
      // Only mail that came in. What Ace or the team sent isn't new work.
      for (const m of t.messages.filter(msg => msg.direction === 'inbound')) {
        out.push({
          // Keep what Ace read off a seeded email (confidence, revised RDQ
          // quantities), then the live thread's fields.
          ...seedEmailById.get(m.id),
          id: m.id,
          from: m.from,
          fromEmail: m.fromEmail,
          to: m.to,
          cc: m.cc,
          subject: t.subject,
          date: m.date,
          body: m.body,
          relatedJobId: t.jobNumbers[0],
        });
      }
    }
    return [...out, ...followUpReport.emails];
  }, [threads, followUpReport]);

  const persistOverlayPatch = useCallback((threadId: string, patch: Partial<EmailThread> & { newMessage?: EmailMessage }) => {
    setOverlay(prev => {
      const existing = prev.find(t => t.id === threadId);
      let next: EmailThread[];
      if (existing) {
        next = prev.map(t => t.id === threadId
          ? { ...t, messages: patch.newMessage ? [...t.messages, patch.newMessage] : t.messages }
          : t);
      } else {
        next = [...prev, {
          id: threadId,
          origin: patch.origin!,
          subject: patch.subject!,
          jobNumbers: patch.jobNumbers!,
          messages: patch.newMessage ? [patch.newMessage] : [],
        }];
      }
      writeThrough(next);
      return next;
    });
  }, []);

  const appendReply = useCallback((threadId: string, entry: { from: string; fromEmail: string; to: string; subject?: string; body: string }) => {
    const message: EmailMessage = {
      id: uid(), direction: 'outbound', from: entry.from, fromEmail: entry.fromEmail,
      to: entry.to, cc: [], date: nowStamp(), body: entry.body, subject: entry.subject,
    };
    persistOverlayPatch(threadId, { newMessage: message });
  }, [persistOverlayPatch]);

  const appendOutbound = useCallback((entry: {
    refKey: string; jobIds: string[]; origin: 'proxy' | 'fulfillment'; to: string; cc?: string[]; subject: string; body: string;
    from?: string; fromEmail?: string; threadId?: string;
  }) => {
    // Fold into an existing thread for a shared job number + origin (e.g.
    // Wayne's seeded solicitor reply) rather than starting a disconnected
    // second one; falls back to refKey when there's no job # yet (a jobless
    // escalation still needs its own thread, not one shared with every other
    // jobless item).
    setOverlay(prevOverlay => {
      // Proxy mail threads by conversation (same subject), so a sent email
      // never lands on an unrelated thread that happens to share a job.
      // Fulfillment's hand-offs still fold by job, as its flows expect.
      const key = conversationKey(entry.subject);
      const sharesJob = (t: EmailThread) => t.origin === entry.origin && (entry.origin === 'proxy'
        ? conversationKey(t.subject) === key
        : t.jobNumbers.some(j => entry.jobIds.includes(j)));
      // A forward of one specific email (the ICT-2 audit) stays on that thread.
      const seedMatch = entry.threadId
        ? seed.find(t => t.id === entry.threadId)
        : entry.jobIds.length || entry.origin === 'proxy' ? seed.find(sharesJob) : undefined;
      // An overlay thread already created by an earlier, independent
      // appendOutbound call for the same job (e.g. the "Received Short"
      // handoff, followed later by SHORTAGE_FOLLOWUP's solicitor outreach) —
      // matched by job overlap, not by this call's own refKey, which can
      // legitimately differ between callers (a scan # here, a job id there).
      const overlayMatch = entry.threadId
        ? prevOverlay.find(t => t.id === entry.threadId)
        : entry.jobIds.length || entry.origin === 'proxy' ? prevOverlay.find(sharesJob) : undefined;
      const threadId = seedMatch?.id ?? overlayMatch?.id
        ?? (entry.jobIds.length
          ? `outbound-job-${[...entry.jobIds].sort().join(',')}-${entry.origin}${entry.origin === 'proxy' ? `-${key.replace(/[^a-z0-9]+/g, '-').slice(0, 60)}` : ''}`
          : `outbound-ref-${entry.refKey}-${entry.origin}`);
      const fromEmail = entry.fromEmail ?? (entry.origin === 'fulfillment' ? TEAM_MAILBOX.wayne : TEAM_MAILBOX.cary);
      const message: EmailMessage = {
        // Sent in under another team's name (Fulfillment's Received Short
        // hand-off), it's incoming mail for this inbox.
        id: uid(), direction: entry.from ? 'inbound' : 'outbound', from: entry.from ?? 'Ace', fromEmail,
        to: entry.to, cc: entry.cc ?? [], date: nowStamp(), body: entry.body, sentByAce: !entry.from,
      };
      const existing = prevOverlay.find(t => t.id === threadId);
      let next: EmailThread[];
      if (existing) {
        next = prevOverlay.map(t => t.id === threadId ? { ...t, messages: [...t.messages, message] } : t);
      } else if (seedMatch) {
        next = [...prevOverlay, { id: threadId, origin: entry.origin, subject: seedMatch.subject, jobNumbers: seedMatch.jobNumbers, messages: [message] }];
      } else {
        next = [...prevOverlay, { id: threadId, origin: entry.origin, subject: entry.subject, jobNumbers: entry.jobIds, messages: [message] }];
      }
      writeThrough(next);
      return next;
    });
  }, [seed]);

  const value = useMemo(
    () => ({ threads, proxyEmails, appendReply, appendOutbound, deleteThread, restoreThreads, setThreadCategory }),
    [threads, proxyEmails, appendReply, appendOutbound, deleteThread, restoreThreads, setThreadCategory],
  );

  return <EmailInboxContext.Provider value={value}>{children}</EmailInboxContext.Provider>;
}

export function useEmailInbox(): EmailInboxContextValue {
  const ctx = useContext(EmailInboxContext);
  if (!ctx) throw new Error('useEmailInbox must be used inside EmailInboxProvider');
  return ctx;
}
